import React from 'react';
import {
  Button, Tooltip, Typography, useTheme,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import CheckIcon from '@mui/icons-material/Check';
import AlertDialog from '../../../../components/AlertDialog';
import Alert from '../../../../components/Alert';
import ShareButtons from '../../../../components/ShareButtons';
import {
  COMPLEMENT_DISCLOSURE, SHARE_BODY, SHARE_TEXT, SHARE_TITLE,
} from './copy';

type ShareDialogProps = {
  open: boolean;
  url: string;
  expiresAt: string;
  onClose: () => void;
  pageCall?: {
    category: string;
    name: string;
    props?: Record<string, string | undefined>;
  };
};

const expiryLine = (expiresAt: string) => {
  const at = new Date(expiresAt);
  if (Number.isNaN(at.getTime())) return 'El enlace vence en 72 horas.';
  return `El enlace vence el ${at.toLocaleDateString('es-CL', {
    day: 'numeric', month: 'long',
  })} a las ${at.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}.`;
};

export default function ShareDialog({
  open, url, expiresAt, onClose, pageCall,
}: ShareDialogProps) {
  const theme = useTheme();
  const [copied, setCopied] = React.useState(false);
  const copyTextToClipboard = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
  };

  return (
    <AlertDialog
      isTriggered={open}
      onCloseAction={onClose}
      pageCall={pageCall}
      hideSubmitButton
      maxWidth="xs"
      closeButtonLabel="Listo"
      dialogTitle={(
        <Typography variant="subtitle2" fontWeight="bold" align="center" component="div">
          {SHARE_TITLE}
        </Typography>
      )}
      dialogContentStyle={{ paddingTop: 8, paddingBottom: 8 }}
      dialogContent={(
        <>
          <Typography
            variant="caption"
            align="center"
            component="div"
            gutterBottom
            style={{ fontSize: 12, lineHeight: 1.4 }}
          >
            {SHARE_BODY}
          </Typography>
          <div style={{ display: 'flex', justifyContent: 'center', margin: '15px 0' }}>
            <Alert
              button={(
                <Tooltip title={copied ? 'Enlace copiado' : 'Copiar enlace'}>
                  <Button
                    onClick={copyTextToClipboard}
                    style={{
                      padding: 15, display: 'flex', placeItems: 'space-between', width: '70%',
                    }}
                    variant="contained"
                    color="primary"
                  >
                    {copied
                      ? (
                        <CheckIcon
                          style={{
                            color: theme.palette.common.white, fontSize: '25px', paddingRight: 5,
                          }}
                        />
                      )
                      : (
                        <LinkIcon
                          style={{
                            color: theme.palette.common.white, fontSize: '25px', paddingRight: 5,
                          }}
                        />
                      )}
                    <span>{copied ? '¡Copiado!' : 'Copiar'}</span>
                  </Button>
                </Tooltip>
              )}
              successMessage="Has copiado el enlace con éxito"
              errorMessage="Ha ocurrido un error copiando el texto"
              action={copyTextToClipboard}
            />
          </div>
          <ShareButtons
            shareText={SHARE_TEXT}
            url={url}
            twitter={false}
            telegram={false}
            facebook={false}
            linkedin={false}
          />
          <Typography
            variant="caption"
            align="center"
            component="div"
            style={{ marginTop: 12, fontSize: 11 }}
          >
            {expiryLine(expiresAt)}
          </Typography>
        </>
      )}
      legend={(
        <Typography variant="caption" component="div" style={{ fontSize: 11, lineHeight: 1.35 }}>
          {COMPLEMENT_DISCLOSURE}
        </Typography>
      )}
    />
  );
}

ShareDialog.defaultProps = {
  pageCall: undefined,
};
